
import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDoc, onSnapshot,
  query, where, orderBy, serverTimestamp, writeBatch, setDoc
} from 'firebase/firestore';
import { db, markSpotAsInvalid } from './firebaseService';
import {
  UserContent, ContentType, WaypointContent, TrackContent,
  LineContent, AreaContent, UserContentFolder, WaypointIcon, AppUser
} from '../types';
import { getDistance } from './geometryUtils';
import { waypointToSpotMapping } from './waypointService';
import { deleteTrack } from './trackService';
import { BRAND } from '../constants/mapColors';

// -------------------------------------------------------
// Collections
// -------------------------------------------------------

const CONTENT_COL = 'user_content';
const FOLDER_COL = 'user_content_folders';

// Lista i «Mitt innhold» er en sammenslåing av fire kilder (se useMyContent).
// Tre av dem bygges bro inn med id-prefiks, og prefikset er det eneste sporet
// av hvor dokumentet faktisk ligger. Skriver vi mot user_content for en
// bro-bygget id, treffer vi et dokument som ikke finnes — reglene ser da
// `resource == null` og svarer permission-denied.
const LEGACY_WAYPOINT_PREFIX = 'legacy_wp_';
const LEGACY_TRACK_PREFIX = 'legacy_track_';
const VISIT_PREFIX = 'visit_';

export type ContentSource = 'content' | 'legacyWaypoint' | 'legacyTrack' | 'visit';

export interface ContentRef {
  source: ContentSource;
  /** null for `visit`, som er syntetisk og ikke har noe dokument. */
  collection: string | null;
  docId: string;
}

export function resolveContentRef(contentId: string): ContentRef {
  if (contentId.startsWith(LEGACY_WAYPOINT_PREFIX)) {
    return {
      source: 'legacyWaypoint',
      collection: 'user_waypoints',
      docId: contentId.slice(LEGACY_WAYPOINT_PREFIX.length),
    };
  }
  if (contentId.startsWith(LEGACY_TRACK_PREFIX)) {
    return {
      source: 'legacyTrack',
      collection: 'user_tracks',
      docId: contentId.slice(LEGACY_TRACK_PREFIX.length),
    };
  }
  if (contentId.startsWith(VISIT_PREFIX)) {
    return { source: 'visit', collection: null, docId: contentId.slice(VISIT_PREFIX.length) };
  }
  return { source: 'content', collection: CONTENT_COL, docId: contentId };
}

// -------------------------------------------------------
// Koordinat-koding (linje/flate)
// -------------------------------------------------------
// Firestore avviser array-i-array. Linjer og flater ble lagret som
// `coordinates: [[lat,lon],[lat,lon],…]`, så hver eneste addDoc kastet
// «Nested arrays are not supported» — tegneverktøyet og GPX-import av linjer
// har aldri fått lagret noe. Vi lagrer derfor flatt i `coordsFlat`:
// [lat,lon,lat,lon,…], og hydrerer tilbake til par ved lesing.
//
// `coordinates: []` skrives ved siden av med vilje. Gamle OTA-bundler er
// fortsatt i bruk (feilrapporter viser helt ned til 1.0.237), og de gjør
// `item.coordinates.map(...)` uten guard. Uten feltet ville de fått en
// TypeError og krasjet kartet; tom array gir dem en usynlig tegning i stedet.
export function encodeCoordinates(coords: [number, number][]): number[] {
  const flat: number[] = [];
  for (const [lat, lon] of coords) flat.push(lat, lon);
  return flat;
}

export function decodeCoordinates(raw: unknown): [number, number][] {
  if (!Array.isArray(raw)) return [];
  // Allerede parvis — tolereres så en eventuell fremtidig/importert doc leses riktig.
  if (Array.isArray(raw[0])) {
    return (raw as unknown[])
      .filter((p): p is number[] => Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number')
      .map(p => [p[0], p[1]] as [number, number]);
  }
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < raw.length; i += 2) {
    const lat = raw[i];
    const lon = raw[i + 1];
    if (typeof lat === 'number' && typeof lon === 'number') out.push([lat, lon]);
  }
  return out;
}

/** Feltene en linje/flate skriver for koordinater. Én kilde for alle skrivestier. */
function coordinateFields(coords: [number, number][]): Record<string, unknown> {
  return { coordsFlat: encodeCoordinates(coords), coordinates: [] };
}

/** Doc → UserContent. Linjer/flater får `coordinates` hydrert fra `coordsFlat`. */
export function hydrateContentDoc(id: string, data: Record<string, any>): UserContent {
  const item: Record<string, any> = { id, ...data };
  if (item.type === 'line' || item.type === 'area') {
    item.coordinates = decodeCoordinates(item.coordsFlat ?? item.coordinates);
  }
  return item as UserContent;
}

// -------------------------------------------------------
// Subscribe — real-time listeners
// -------------------------------------------------------

export function subscribeToUserContent(
  userId: string,
  callback: (content: UserContent[]) => void
): () => void {
  const q = query(
    collection(db, CONTENT_COL),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map(d => hydrateContentDoc(d.id, d.data() as Record<string, any>));
    callback(items);
  }, () => callback([]));
}

export function subscribeToUserFolders(
  userId: string,
  callback: (folders: UserContentFolder[]) => void
): () => void {
  const q = query(
    collection(db, FOLDER_COL),
    where('userId', '==', userId),
    orderBy('sortOrder', 'asc')
  );
  return onSnapshot(q, (snap) => {
    const folders = snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
    })) as UserContentFolder[];
    callback(folders);
  }, () => callback([]));
}

// -------------------------------------------------------
// Content CRUD
// -------------------------------------------------------

export async function addWaypointContent(
  userId: string,
  latitude: number,
  longitude: number,
  name: string,
  icon: WaypointIcon = 'pin',
  color: string = '#ef4444',
  note?: string,
  folderId?: string | null,
  photoUrl?: string | null,
  linkedSpotId?: string | null,
  features?: string[] | null,
  spotMetadata?: {
    spotType?: string;
    suitabilityColor?: string;
    barrierMetadata?: any;
    attractionCategory?: string;
    description?: string;
    website?: string;
    videoUrl?: string;
  } | null
): Promise<string> {
  const now = Date.now();
  const data: Record<string, any> = {
    userId,
    type: 'waypoint' as ContentType,
    name: name.trim() || 'Mitt punkt',
    note: note?.trim() || null,
    icon,
    color,
    latitude,
    longitude,
    folderId: folderId ?? null,
    createdAt: now,
    updatedAt: now,
    bounds: { latMin: latitude, latMax: latitude, lonMin: longitude, lonMax: longitude },
    _serverTs: serverTimestamp(),
  };
  if (photoUrl) data.photoUrl = photoUrl;
  if (linkedSpotId) data.linkedSpotId = linkedSpotId;
  if (features && features.length > 0) data.features = features;
  if (spotMetadata && Object.keys(spotMetadata).length > 0) {
    // Strip undefined to avoid Firestore errors
    const cleanMeta: Record<string, any> = {};
    for (const [k, v] of Object.entries(spotMetadata)) {
      if (v !== undefined && v !== null) cleanMeta[k] = v;
    }
    if (Object.keys(cleanMeta).length > 0) data.spotMetadata = cleanMeta;
  }
  const ref = await addDoc(collection(db, CONTENT_COL), data);
  return ref.id;
}

export async function addTrackContent(
  userId: string,
  name: string,
  distance: number,
  duration: number,
  elevationGain: number,
  elevationLoss: number,
  pointCount: number,
  gpxUrl: string,
  startLocation: { latitude: number; longitude: number },
  bounds: { latMin: number; latMax: number; lonMin: number; lonMax: number },
  color: string = BRAND,
  folderId?: string | null
): Promise<string> {
  const now = Date.now();
  const ref = await addDoc(collection(db, CONTENT_COL), {
    userId,
    type: 'track' as ContentType,
    name: name.trim() || 'Min tur',
    color,
    distance: Math.round(distance),
    duration,
    elevationGain,
    elevationLoss,
    pointCount,
    gpxUrl,
    startLocation,
    bounds,
    folderId: folderId ?? null,
    createdAt: now,
    updatedAt: now,
    _serverTs: serverTimestamp(),
  });
  return ref.id;
}

export async function addLineContent(
  userId: string,
  name: string,
  coordinates: [number, number][],
  color: string = '#3b82f6',
  strokeWidth: number = 4,
  strokeStyle: 'solid' | 'dashed' | 'dotted' = 'solid',
  note?: string,
  folderId?: string | null
): Promise<string> {
  const now = Date.now();
  const distance = calculatePolylineDistance(coordinates);
  const bounds = calculateBounds(coordinates);
  const ref = await addDoc(collection(db, CONTENT_COL), {
    userId,
    type: 'line' as ContentType,
    name: name.trim() || 'Min linje',
    note: note?.trim() || null,
    color,
    ...coordinateFields(coordinates),
    distance,
    strokeWidth,
    strokeStyle,
    bounds,
    folderId: folderId ?? null,
    createdAt: now,
    updatedAt: now,
    _serverTs: serverTimestamp(),
  });
  return ref.id;
}

export async function addAreaContent(
  userId: string,
  name: string,
  coordinates: [number, number][],
  color: string = '#8b5cf6',
  fillOpacity: number = 0.15,
  strokeWidth: number = 3,
  note?: string,
  folderId?: string | null
): Promise<string> {
  const now = Date.now();
  const areaSqm = calculatePolygonArea(coordinates);
  const bounds = calculateBounds(coordinates);
  const ref = await addDoc(collection(db, CONTENT_COL), {
    userId,
    type: 'area' as ContentType,
    name: name.trim() || 'Mitt område',
    note: note?.trim() || null,
    color,
    ...coordinateFields(coordinates),
    areaSqm,
    fillOpacity,
    strokeWidth,
    bounds,
    folderId: folderId ?? null,
    createdAt: now,
    updatedAt: now,
    _serverTs: serverTimestamp(),
  });
  return ref.id;
}

export async function updateContent(
  contentId: string,
  fields: Partial<Pick<UserContent, 'name' | 'note' | 'color' | 'folderId'>>
): Promise<void> {
  await updateDoc(doc(db, CONTENT_COL, contentId), {
    ...fields,
    updatedAt: Date.now(),
    _serverTs: serverTimestamp(),
  });

  // Hvis dette er et waypoint med linket spot, propager navn/notat-endringer til den delte plassen
  // slik at andre brukere ser samme tekst som eieren.
  if (fields.name === undefined && fields.note === undefined) return;
  try {
    const snap = await getDoc(doc(db, CONTENT_COL, contentId));
    const data = snap.data();
    const linkedSpotId = data?.linkedSpotId;
    if (!linkedSpotId || data?.type !== 'waypoint') return;
    const spotPatch: Record<string, any> = {};
    if (fields.name !== undefined) spotPatch.name = fields.name;
    if (fields.note !== undefined) spotPatch.description = fields.note ?? '';
    if (Object.keys(spotPatch).length > 0) {
      await setDoc(doc(db, 'cached_spots', linkedSpotId), spotPatch, { merge: true });
    }
  } catch (err) {
    console.warn('[updateContent] linked spot sync failed:', err);
  }
}

export async function updateLineCoordinates(
  contentId: string,
  coordinates: [number, number][]
): Promise<void> {
  const distance = calculatePolylineDistance(coordinates);
  const bounds = calculateBounds(coordinates);
  await updateDoc(doc(db, CONTENT_COL, contentId), {
    ...coordinateFields(coordinates),
    distance,
    bounds,
    updatedAt: Date.now(),
    _serverTs: serverTimestamp(),
  });
}

export async function updateAreaCoordinates(
  contentId: string,
  coordinates: [number, number][]
): Promise<void> {
  const areaSqm = calculatePolygonArea(coordinates);
  const bounds = calculateBounds(coordinates);
  await updateDoc(doc(db, CONTENT_COL, contentId), {
    ...coordinateFields(coordinates),
    areaSqm,
    bounds,
    updatedAt: Date.now(),
    _serverTs: serverTimestamp(),
  });
}

/**
 * Publiser et eksisterende privat waypoint som en delt Fricamp-plass.
 * Oppretter en ny `cached_spots`-doc med createdBy + sourceLevel:4 og
 * lenker waypointet via `linkedSpotId`. Returnerer den nye spot-ID-en.
 *
 * Hvis waypointet allerede har linkedSpotId, returneres den eksisterende ID-en
 * uten å skrive noe nytt (idempotent).
 */
export async function publishWaypointAsSpot(
  waypoint: WaypointContent,
  user: AppUser
): Promise<{ spotId: string; spot: Record<string, any> | null }> {
  if (waypoint.linkedSpotId) return { spotId: waypoint.linkedSpotId, spot: null };

  const mapping = waypointToSpotMapping(waypoint.name, waypoint.note, waypoint.icon);
  const spotId = `wp-${waypoint.id}-${Date.now().toString(36)}`;
  const now = Date.now();

  const spotData: Record<string, any> = {
    id: spotId,
    source: 'user',
    importVersion: 'waypoint-publish',
    lastSyncedAt: new Date(now).toISOString(),
    name: waypoint.name || 'Mitt punkt',
    description: waypoint.note || '',
    location: { latitude: waypoint.latitude, longitude: waypoint.longitude },
    score: 7,
    spotType: mapping.spotType,
    sourceLevel: 4,
    suitabilityColor: mapping.suitabilityColor,
    features: ['BRUKERBIDRAG', ...mapping.features],
    reasoning: 'Publisert fra eget waypoint.',
    trafficRisk: 'Ukjent',
    buildingProximity: 'Ukjent',
    nearestHouse: 999,
    highway: 'Ukjent',
    surfaceType: 'Ukjent',
    isPublicAccess: true,
    legal: { isUtmark: true, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false },
    createdBy: user.uid,
    createdByName: user.displayName || 'Anonym',
    createdAt: now,
    isVerified: !!waypoint.photoUrl,
    verifiedAt: waypoint.photoUrl ? now : undefined,
    photoUrls: waypoint.photoUrl ? [waypoint.photoUrl] : [],
    visibility: 'public',
  };
  if (user.photoURL) spotData.createdByPhoto = user.photoURL;

  await setDoc(doc(db, 'cached_spots', spotId), spotData);

  // Lenke waypointet til den nye spotten. Tre opphav:
  // 1. user_content — vanlig case, oppdater doc'en
  // 2. legacy_wp_<id> — bridge fra user_waypoints, oppdater den underliggende doc'en
  // 3. visit_<id> — bridge fra besøk, kan ikke oppdateres (auto-genereres)
  if (waypoint.id.startsWith('legacy_wp_')) {
    const realId = waypoint.id.slice('legacy_wp_'.length);
    try {
      await updateDoc(doc(db, 'user_waypoints', realId), {
        linkedSpotId: spotId,
      });
    } catch (err) {
      console.warn('[publishWaypointAsSpot] could not link legacy waypoint:', err);
    }
  } else if (!waypoint.id.startsWith('visit_')) {
    await updateDoc(doc(db, CONTENT_COL, waypoint.id), {
      linkedSpotId: spotId,
      updatedAt: now,
      _serverTs: serverTimestamp(),
    });
  }

  return { spotId, spot: spotData };
}

export async function deleteContent(
  contentId: string,
  options?: { alsoDeleteLinkedSpot?: boolean }
): Promise<void> {
  const ref = resolveContentRef(contentId);

  // Besøk er syntetiske rader bygget fra SpotVisit — det finnes ikke noe
  // dokument å slette, og et forsøk ville uansett blitt avvist.
  if (!ref.collection) return;

  if (options?.alsoDeleteLinkedSpot) {
    try {
      const snap = await getDoc(doc(db, ref.collection, ref.docId));
      const linkedSpotId = snap.data()?.linkedSpotId;
      if (linkedSpotId) {
        // Routes through markSpotAsInvalid so both cached_spots and spots
        // collections are flagged — otherwise news feed (which reads from
        // spots) keeps showing cascade-deleted waypoints.
        await markSpotAsInvalid(linkedSpotId, 'Slettet av eier (cascade fra waypoint)');
      }
    } catch (err) {
      console.warn('[deleteContent] linked spot cascade failed:', err);
    }
  }

  // Gamle turer har en GPX-fil i Storage ved siden av dokumentet. deleteTrack
  // rydder begge; sletter vi bare doc'en blir filen liggende igjen for alltid.
  if (ref.source === 'legacyTrack') {
    let gpxUrl = '';
    try {
      const snap = await getDoc(doc(db, ref.collection, ref.docId));
      gpxUrl = snap.data()?.gpxUrl || '';
    } catch (err) {
      console.warn('[deleteContent] could not read legacy track gpxUrl:', err);
    }
    await deleteTrack(ref.docId, gpxUrl);
    return;
  }

  await deleteDoc(doc(db, ref.collection, ref.docId));
}

export interface MoveToFolderResult {
  moved: number;
  /** Id-er som ikke kan ligge i mapper (bro-bygde legacy-elementer og besøk). */
  skipped: string[];
}

export async function moveToFolder(
  contentIds: string[],
  folderId: string | null
): Promise<MoveToFolderResult> {
  // Bare user_content-dokumenter har et folderId-felt. Legacy-elementer og
  // besøk ville gitt permission-denied på hele batchen, slik at ingenting ble
  // flyttet — derfor siles de ut og rapporteres tilbake i stedet.
  const movable: string[] = [];
  const skipped: string[] = [];
  for (const id of contentIds) {
    if (resolveContentRef(id).source === 'content') movable.push(id);
    else skipped.push(id);
  }

  if (movable.length > 0) {
    const batch = writeBatch(db);
    for (const id of movable) {
      batch.update(doc(db, CONTENT_COL, id), {
        folderId: folderId ?? null,
        updatedAt: Date.now(),
      });
    }
    await batch.commit();
  }

  return { moved: movable.length, skipped };
}

// -------------------------------------------------------
// Folder CRUD
// -------------------------------------------------------

export async function createFolder(
  userId: string,
  name: string,
  color: string = '#6b7280',
  sortOrder: number = 0
): Promise<string> {
  const now = Date.now();
  const ref = await addDoc(collection(db, FOLDER_COL), {
    userId,
    name: name.trim() || 'Ny mappe',
    color,
    parentId: null,
    sortOrder,
    createdAt: now,
    updatedAt: now,
    _serverTs: serverTimestamp(),
  });
  return ref.id;
}

export async function updateFolder(
  folderId: string,
  fields: Partial<Pick<UserContentFolder, 'name' | 'color' | 'sortOrder'>>
): Promise<void> {
  await updateDoc(doc(db, FOLDER_COL, folderId), {
    ...fields,
    updatedAt: Date.now(),
  });
}

export async function deleteFolder(folderId: string): Promise<void> {
  await deleteDoc(doc(db, FOLDER_COL, folderId));
}

// -------------------------------------------------------
// Geometry helpers
// -------------------------------------------------------

function calculatePolylineDistance(coords: [number, number][]): number {
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    total += getDistance(coords[i][0], coords[i][1], coords[i + 1][0], coords[i + 1][1]);
  }
  return Math.round(total);
}

export function calculatePolygonArea(coords: [number, number][]): number {
  if (coords.length < 3) return 0;
  const R = 6371e3;
  let area = 0;
  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length;
    const lat1 = coords[i][0] * Math.PI / 180;
    const lat2 = coords[j][0] * Math.PI / 180;
    const dLon = (coords[j][1] - coords[i][1]) * Math.PI / 180;
    area += dLon * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  return Math.abs(area * R * R / 2);
}

function calculateBounds(coords: [number, number][]): {
  latMin: number; latMax: number; lonMin: number; lonMax: number;
} {
  let latMin = Infinity, latMax = -Infinity, lonMin = Infinity, lonMax = -Infinity;
  for (const [lat, lon] of coords) {
    if (lat < latMin) latMin = lat;
    if (lat > latMax) latMax = lat;
    if (lon < lonMin) lonMin = lon;
    if (lon > lonMax) lonMax = lon;
  }
  return { latMin, latMax, lonMin, lonMax };
}

// -------------------------------------------------------
// Measurement formatting
// -------------------------------------------------------

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

export function formatArea(sqMeters: number): string {
  if (sqMeters < 10000) return `${Math.round(sqMeters)} m\u00B2`;
  if (sqMeters < 1000000) return `${(sqMeters / 10000).toFixed(1)} daa`;
  return `${(sqMeters / 1000000).toFixed(2)} km\u00B2`;
}

// -------------------------------------------------------
// GPX export
// -------------------------------------------------------

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function exportContentAsGpx(items: UserContent[]): string {
  const now = new Date().toISOString();
  let wpts = '';
  let rtes = '';
  let trks = '';

  for (const item of items) {
    switch (item.type) {
      case 'waypoint': {
        wpts += `  <wpt lat="${item.latitude}" lon="${item.longitude}">\n`;
        wpts += `    <name>${escapeXml(item.name)}</name>\n`;
        if (item.note) wpts += `    <desc>${escapeXml(item.note)}</desc>\n`;
        wpts += `  </wpt>\n`;
        break;
      }
      case 'line': {
        rtes += `  <rte>\n    <name>${escapeXml(item.name)}</name>\n`;
        if (item.note) rtes += `    <desc>${escapeXml(item.note)}</desc>\n`;
        for (const [lat, lon] of item.coordinates) {
          rtes += `    <rtept lat="${lat}" lon="${lon}" />\n`;
        }
        rtes += `  </rte>\n`;
        break;
      }
      case 'area': {
        // Export area as a closed route
        rtes += `  <rte>\n    <name>${escapeXml(item.name)}</name>\n`;
        if (item.note) rtes += `    <desc>${escapeXml(item.note)}</desc>\n`;
        const pts = [...item.coordinates];
        // Close the ring if not already closed
        if (pts.length > 0) {
          const first = pts[0];
          const last = pts[pts.length - 1];
          if (first[0] !== last[0] || first[1] !== last[1]) pts.push(first);
        }
        for (const [lat, lon] of pts) {
          rtes += `    <rtept lat="${lat}" lon="${lon}" />\n`;
        }
        rtes += `  </rte>\n`;
        break;
      }
      case 'track': {
        trks += `  <trk>\n    <name>${escapeXml(item.name)}</name>\n`;
        trks += `    <trkseg>\n`;
        // Tracks don't have inline coordinates — only a gpxUrl
        // We include start location as a single point
        if (item.startLocation) {
          trks += `      <trkpt lat="${item.startLocation.latitude}" lon="${item.startLocation.longitude}" />\n`;
        }
        trks += `    </trkseg>\n  </trk>\n`;
        break;
      }
    }
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Fricamp" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>Fricamp eksport</name>
    <time>${now}</time>
  </metadata>
${wpts}${rtes}${trks}</gpx>`;
}

export function downloadGpxFile(gpxContent: string, filename: string = 'fricamp-export.gpx'): void {
  const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
